import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { db } from '../firebase';
import { collection, query, where, getDocs, getDoc, doc, orderBy } from 'firebase/firestore';
import ArtListing from '../components/ArtListing';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem 2rem;
`;

const ArtistHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 2rem;
  gap: 1.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const ArtistImage = styled.img`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  object-fit: cover;
`;

const ArtistImagePlaceholder = styled.div`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background-color: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  color: #aaa;
`;

const ArtistInfo = styled.div`
  flex: 1;
`;

const ArtistName = styled.h1`
  margin: 0 0 0.5rem 0;
  font-size: 2rem;
`;

const ArtistBio = styled.p`
  margin: 0 0 0.5rem 0;
  color: #666;
  font-size: 1rem;
  max-width: 800px;
`;

const ExhibitionsSection = styled.div`
  margin: 1.5rem 0;
`;

const ExhibitionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
`;

const ExhibitionBanner = styled.div`
  position: relative;
  width: 100%;
  padding-top: 56.25%; // 16:9 aspect ratio
  cursor: pointer;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.02);
  }
`;

const ExhibitionBannerImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const ArtistLocation = styled.div`
  font-size: 0.9rem;
  color: #666;
`;

const SocialLinks = styled.div`
  margin-top: 0.5rem;
  
  a {
    color: #2196f3;
    text-decoration: none;
    margin-right: 1rem;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const Title = styled.h2`
  margin-bottom: 1.5rem;
  font-size: 1.5rem;
`;

const ArtGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 2rem;
`;

const LoadingIndicator = styled.div`
  text-align: center;
  padding: 2rem;
  font-size: 1.2rem;
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 3rem;
  font-size: 1.2rem;
  color: #666;
`;

const FilterSection = styled.div`
  margin-bottom: 2rem;
`;

const FilterButtons = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const FilterButton = styled.button`
  padding: 0.5rem 1rem;
  background-color: ${props => props.$active ? '#000' : 'transparent'};
  color: ${props => props.$active ? '#fff' : '#000'};
  border: 1px solid #000;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  
  &:hover {
    background-color: ${props => props.$active ? '#000' : '#f0f0f0'};
  }
`;

const ArtistWorksScreen = () => {
  const { artistId } = useParams();
  const navigate = useNavigate();
  const [artist, setArtist] = useState(null);
  const [artworks, setArtworks] = useState([]);
  const [exhibitions, setExhibitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all', 'forSale', 'sold'

  useEffect(() => {
    const fetchArtistAndWorks = async () => {
      if (!artistId) {
        setError('Invalid artist data');
        setLoading(false);
        return;
      }

      try {
        // Fetch artist data
        const artistRef = doc(db, 'users', artistId);
        const artistDoc = await getDoc(artistRef);
        
        if (!artistDoc.exists()) {
          throw new Error('Artist not found');
        }
        
        const artistData = { id: artistDoc.id, ...artistDoc.data() };
        setArtist(artistData);

        // Fetch exhibitions if artist has any
        if (artistData.exhibitions && artistData.exhibitions.length > 0) {
          const exhibitionsList = await Promise.all(
            artistData.exhibitions.map(async (exhibitionRef) => {
              const exhibitionDoc = await getDoc(exhibitionRef);
              if (exhibitionDoc.exists()) {
                return {
                  id: exhibitionDoc.id,
                  ...exhibitionDoc.data()
                };
              }
              return null;
            })
          );
          const validExhibitions = exhibitionsList.filter(exhibition => exhibition !== null);
          setExhibitions(validExhibitions);
        }

        // Fetch artworks
        const artworksRef = collection(db, 'artworks');
        const q = query(
          artworksRef, 
          where('artist', '==', artistRef),
          orderBy('createdAt', 'desc')
        );
        
        const querySnapshot = await getDocs(q);
        const artworksList = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          artistData: artistData // Attach full artist data to each artwork
        }));
        
        setArtworks(artworksList);
      } catch (error) {
        console.error("Error fetching artist and works:", error);
        setError('Failed to fetch artworks');
      } finally {
        setLoading(false);
      }
    };

    fetchArtistAndWorks();
  }, [artistId]);

  const handleArtworkClick = (artwork) => {
    navigate(`/artwork/${artwork.id}`);
  };

  const getInitials = () => {
    if (artist?.displayName) {
      return artist.displayName
        .split(' ')
        .map(name => name[0])
        .join('')
        .toUpperCase();
    }
    return '';
  };

  const getFilteredArtworks = () => {
    if (filter === 'all') {
      return artworks;
    } else if (filter === 'forSale') {
      return artworks.filter(artwork => artwork.isForSale);
    } else if (filter === 'sold') {
      return artworks.filter(artwork => !artwork.isForSale);
    }
    return artworks;
  };

  if (loading) {
    return <LoadingIndicator>Loading artist profile...</LoadingIndicator>;
  }

  if (error) {
    return <EmptyMessage>{error}</EmptyMessage>;
  }

  return (
    <Container>
      <ArtistHeader>
        {artist.profilePhoto ? (
          <ArtistImage src={artist.profilePhoto} alt={artist.displayName} />
        ) : (
          <ArtistImagePlaceholder>{getInitials()}</ArtistImagePlaceholder>
        )}
        
        <ArtistInfo>
          <ArtistName>{artist.displayName}</ArtistName>
          
          <ArtistLocation>
            {artist.birthYear && <span>b. {artist.birthYear}</span>}
            {artist.birthYear && artist.location && <span> • </span>}
            {artist.location && <span>{artist.location}</span>}
          </ArtistLocation>
          
          <ArtistBio>{artist.bio || 'No artist bio available.'}</ArtistBio>
          
          <SocialLinks>
            {artist.instagramHandle && (
              <a 
                href={`https://instagram.com/${artist.instagramHandle}`} 
                target="_blank" 
                rel="noopener noreferrer"
              >
                Instagram
              </a>
            )}
            
            {artist.websiteURL && (
              <a 
                href={artist.websiteURL} 
                target="_blank" 
                rel="noopener noreferrer"
              >
                Website
              </a>
            )}
          </SocialLinks>
        </ArtistInfo>
      </ArtistHeader>

      {exhibitions.length > 0 && (
        <ExhibitionsSection>
          <Title>Current Exhibitions</Title>
          <ExhibitionsGrid>
            {exhibitions.map((exhibition) => (
              <ExhibitionBanner 
                key={exhibition.id}
                onClick={() => navigate(`/exhibition/${exhibition.id}`)}
              >
                {exhibition.banner && (
                  <ExhibitionBannerImage 
                    src={exhibition.banner} 
                    alt={exhibition.name}
                  />
                )}
              </ExhibitionBanner>
            ))}
          </ExhibitionsGrid>
        </ExhibitionsSection>
      )}

      <FilterSection>
        <FilterButtons>
          <FilterButton 
            $active={filter === 'all'} 
            onClick={() => setFilter('all')}
          >
            All Works
          </FilterButton>
          <FilterButton 
            $active={filter === 'forSale'} 
            onClick={() => setFilter('forSale')}
          >
            For Sale
          </FilterButton>
          <FilterButton 
            $active={filter === 'sold'} 
            onClick={() => setFilter('sold')}
          >
            Sold
          </FilterButton>
        </FilterButtons>
      </FilterSection>

      {getFilteredArtworks().length === 0 ? (
        <EmptyMessage>No artworks found for this artist</EmptyMessage>
      ) : (
        <ArtGrid>
          {getFilteredArtworks().map(artwork => (
            <ArtListing 
              key={artwork.id} 
              artwork={artwork} 
              onClick={() => handleArtworkClick(artwork)} 
            />
          ))}
        </ArtGrid>
      )}
    </Container>
  );
};

export default ArtistWorksScreen;